import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseApiQueryOptions<T> {
  enabled?: boolean;
  onError?: (err: Error) => void;
  initialData?: T;
}

export interface UseApiQueryResult<T> {
  data: T | undefined;
  error: Error | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

function serializeKey(key: unknown[]): string {
  try {
    return JSON.stringify(key);
  } catch {
    return String(key);
  }
}

function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  return new Error(typeof err === 'string' ? err : 'Unknown error');
}

/**
 * Hook for GET-style API calls. Replaces the useState+useEffect+fetch+abort
 * pattern duplicated across dashboard/settings/admin views.
 */
export function useApiQuery<T>(
  key: unknown[],
  fetcher: (signal: AbortSignal) => Promise<T>,
  options: UseApiQueryOptions<T> = {},
): UseApiQueryResult<T> {
  const { enabled = true, onError, initialData } = options;

  const [data, setData] = useState<T | undefined>(initialData);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);

  const fetcherRef = useRef(fetcher);
  // Sync the latest fetcher/onError into refs outside of render. Both refs
  // are only read inside the runFetch callback, which executes after the
  // effect commit phase (react-hooks/refs §2.7).
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const abortRef = useRef<AbortController | null>(null);

  // Track the key that owns the currently in-flight request so StrictMode's
  // double-invoke does not re-fire a fetch for the same key.
  const activeKeyRef = useRef<string | null>(null);

  const serialized = serializeKey(key);

  const runFetch = useCallback(async (keyForRun: string): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    activeKeyRef.current = keyForRun;

    setIsLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current(controller.signal);
      if (controller.signal.aborted) return;
      setData(result);
    } catch (err) {
      if (isAbortError(err) || controller.signal.aborted) return;
      const e = toError(err);
      setError(e);
      onErrorRef.current?.(e);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      // Defer the setIsLoading(false) into a microtask so the effect body
      // itself is free of synchronous setState (react-hooks/set-state-in-effect
      // §2.6). The runFetch path schedules its own setIsLoading inside an
      // async callback so that branch is already compliant.
      Promise.resolve().then(() => {
        setIsLoading(false);
      });
      return;
    }

    if (activeKeyRef.current === serialized && abortRef.current && !abortRef.current.signal.aborted) {
      return;
    }

    void runFetch(serialized);

    return () => {
      abortRef.current?.abort();
    };
  }, [serialized, enabled, runFetch]);

  const refetch = useCallback(async () => {
    await runFetch(serialized);
  }, [runFetch, serialized]);

  return { data, error, isLoading, refetch };
}
